import {
  Badge,
  CollectionPreferencesProps,
  Icon,
  Link,
  Popover,
} from '@amzn/awsui-components-core';
import {
  BarRaiserTechDomains,
  ContentWorkflow,
  QualityCheckStatus,
  TechDomain,
  WorkflowInfo,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React from 'react';
import { extractUserAlias, findPublicationWorkflow } from '../../api/UtilsApi';
import constants from '../../common/constants';
import DeleteContentButton from '../content/DeleteContentButton';
import EditingDraftButton from '../content/EditingDraftButton';
import ProgressDiagram from '../content/ProgressDiagram';
import PublisherContentViewerLink from '../publication/PublisherContentViewerLink';
import {
  booleanComparator,
  dateComparator,
  numberComparator,
  setComparator,
  stringComparator,
} from '../utils/Comparator';
import AuthorContentViewerLink from './AuthorContentViewerLink';
import ContentAudienceBadge from './ContentAudienceBadge';
import FinalizingContentViewerLink from './FinalizingContentViewerLink';
import ReviewerContentViewerLink from './ReviewerContentViewerLink';
import TechWriterContentViewerlink from './TechWriterContentViewerLink';
import WorkflowStateBadge from './WorkflowStateBadge';
import WorkflowViewerAdminLink from './WorkflowViewerAdminLink';
import ContentEngagementAdminLink from '../content-engagement/ContentEngagementAdminLink';
import { Box } from '@amzn/awsui-components-core';
import WorkflowTagBadges from './WorkflowTagBadges';
import { getContentWorkflowTags, getContentWorkflowTagNames } from '../utils/WorkflowTagUtils';
import PostViewerLink from './PostViewerLink';
import { contentPriorityDisplayNames, contentSubTypeDisplayNames } from '../utils/DisplayNames';
import RouterPaths from '../../common/RouterPaths';
import EditMetadataButton from '../content/EditMetadataButton';

const renderWorkflowInfoLink = (wfInfo: WorkflowInfo) => {
  const name = `Version ${wfInfo.workflowId}:`;
  const href = `/content-history-viewer/${wfInfo.contentId}/${wfInfo.workflowId}`;
  if (wfInfo.state === WorkflowState.UNPUBLISHED) {
    return name;
  }
  return (
    <Link variant="secondary" href={href}>
      {name}
    </Link>
  );
};

export const OwnerCol = {
  allowLineWrap: true,
  cell: (i: ContentWorkflow) => (i.ownerProfile ? i.ownerProfile.name : i.ownerId),
  header: 'Author',
  id: 'owner',
};

export const TechWriterOwnerCol = {
  allowLineWrap: true,
  cell: (i: ContentWorkflow) => {
    const ownerAlias = ` (${extractUserAlias(i.ownerId)})`;
    return i.ownerProfile ? `${i.ownerProfile.name}${ownerAlias}` : i.ownerId;
  },
  header: 'Author',
  id: 'owner',
};

export const OwnerSort = {
  comparator: stringComparator((i) => i.ownerId),
  field: 'ownerId',
  id: 'owner',
};

export const OwnerSelOpt = {
  editable: true,
  id: 'owner',
  label: 'Author',
  visible: true,
};

export const AdminContentNameCol = {
  cell: (i: ContentWorkflow) => (
    <WorkflowViewerAdminLink contentId={i.contentId} metadata={i.draftContentMetadata} />
  ),
  header: 'Content title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const AuthorContentNameCol = {
  cell: (i: ContentWorkflow) => <AuthorContentViewerLink contentWorkflow={i} />,
  header: 'Content title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const ReviewerContentNameCol = {
  cell: (i: ContentWorkflow) => (
    <ReviewerContentViewerLink contentId={i.contentId} metadata={i.draftContentMetadata} />
  ),
  header: 'Content title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const IsBarRaiserDomainColumn = {
  cell: (i: ContentWorkflow) => {
    const contentMainTechDomain =
      TechDomain[i.draftTemplateMetadata?.mainTechDomain as keyof typeof TechDomain];
    if (BarRaiserTechDomains.includes(contentMainTechDomain)) {
      return 'Y';
    } else {
      return 'N';
    }
  },
  header: 'Is BarRaiser Domain',
  id: 'isBarRaiserDomain',
};

export const IsBarRaiserDomainSort = {
  comparator: booleanComparator((i) =>
    BarRaiserTechDomains.includes(i.draftTemplateMetadata.mainTechDomain),
  ),
  id: 'isBarRaiserDomain',
};

export const IsBarRaiserDomainSelOpt = {
  editable: true,
  id: 'isBarRaiserDomain',
  label: 'Is BarRaiser Domain',
  visible: true,
};

export const PublisherContentNameCol = {
  cell: (i: ContentWorkflow) => (
    <PublisherContentViewerLink
      contentId={i.contentId}
      metadata={i.draftContentMetadata}
      draftState={i.draftState}
      navigateBackTo={RouterPaths.CONTENT_TO_PUBLISH}
    />
  ),
  header: 'Content title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const TechWriterContentNameCol = {
  cell: (i: ContentWorkflow) => (
    <TechWriterContentViewerlink contentId={i.contentId} metadata={i.draftContentMetadata} />
  ),
  header: 'Content title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const FinalizingContentNameCol = {
  cell: (i: ContentWorkflow) => (
    <FinalizingContentViewerLink
      contentId={i.contentId}
      workflowId={i.publicationWorkflowId}
      metadata={i.draftContentMetadata}
    />
  ),
  header: 'Content title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const PostContentNameCol = {
  cell: (i: ContentWorkflow) => (
    <PostViewerLink contentId={i.contentId} metadata={i.draftContentMetadata} />
  ),
  header: 'Post title',
  id: 'contentName',
  minWidth: 300,
  width: 300,
};

export const ContentAudienceCol = {
  cell: (i: ContentWorkflow) => <ContentAudienceBadge contentAudience={i.contentAudience} />,
  header: 'Intended audience',
  id: 'contentAudience',
};

export const ContentAudienceSort = {
  comparator: stringComparator((i) => i.contentAudience),
  id: 'contentAudience',
};

export const ContentAudienceSelOpt = {
  editable: true,
  id: 'contentAudience',
  label: 'Intended audience',
  visible: true,
};

export const ContentNameSort = {
  comparator: stringComparator((i) => i.draftContentMetadata.contentName),
  id: 'contentName',
};

export const ContentNameSelOpt = {
  editable: false,
  id: 'contentName',
  label: 'Content title',
  visible: true,
};

export const ContentIdCol = {
  cell: (i: ContentWorkflow) => i.contentId,
  header: 'Content id',
  id: 'contentId',
};

export const ContentIdSort = {
  comparator: stringComparator((i) => i.contentId),
  id: 'contentId',
};

export const ContentIdSelOpt = {
  editable: true,
  id: 'contentId',
  label: 'Content id',
  visible: false,
};

export const ContentTypeCol = {
  cell: (i: ContentWorkflow) => i.draftContentMetadata.contentType,
  header: 'Content type',
  id: 'contentType',
};

export const ContentTypeSort = {
  comparator: stringComparator((i) => i.draftContentMetadata.contentType),
  id: 'contentType',
};

export const ContentTypeSelOpt = {
  editable: true,
  id: 'contentType',
  label: 'Content type',
  visible: true,
};

export const ContentSubTypeCol = {
  cell: (i: ContentWorkflow) =>
    contentSubTypeDisplayNames.get(i.draftContentTypeMetadata.contentSubType),
  header: 'Subtype',
  id: 'contentSubType',
};

export const ContentSubTypeSort = {
  comparator: stringComparator((i) => i.draftContentTypeMetadata.contentSubType),
  id: 'contentSubType',
};

export const ContentSubTypeSelOpt = {
  editable: true,
  id: 'contentSubType',
  label: 'Subtype',
  visible: true,
};

export const DraftStateCol = {
  cell: (i: ContentWorkflow) => {
    const qcStatus = i.qualityCheckInfo?.scanStatus;
    const qcConfig = qcStatus ? qualityCheckBadgeConfig.get(qcStatus) : undefined;
    return (
      <>
        <WorkflowStateBadge state={i.draftState} />
        {qcConfig && (
          <>
            {' '}
            <Badge color={qcConfig.color}>{qcConfig.label}</Badge>
          </>
        )}
      </>
    );
  },
  header: 'Draft state',
  id: 'draftstate',
  width: 200,
};

export const DraftStateSort = {
  comparator: stringComparator((i) => i.draftState),
  id: 'draftstate',
};

export const DraftStateSelOpt = {
  editable: true,
  id: 'draftstate',
  label: 'Draft state',
  visible: true,
};

export const DraftVersionCol = {
  cell: (i: ContentWorkflow) => i.draftWorkflowId,
  header: 'Draft version',
  id: 'draftVersion',
};

export const DraftVersionSort = {
  comparator: numberComparator((i) => i.draftWorkflowId),
  id: 'draftVersion',
};

export const DraftVersionSelOpt = {
  editable: true,
  id: 'draftVersion',
  label: 'Draft version',
  visible: false,
};

export const TechWriterCol = {
  cell: (i: ContentWorkflow) => extractUserAlias(i.techWriterId),
  header: 'Assignee',
  id: 'techWriterAssignee',
};

export const TechWriterSort = {
  comparator: stringComparator((i) => i.techWriterId),
  id: 'techWriterAssignee',
};

export const TechWriterSelOpt = {
  editable: true,
  id: 'techWriterAssignee',
  label: 'Assignee',
  visible: true,
};

export const TechWriterQueueCol = {
  cell: (i: ContentWorkflow) => i.publicationDate?.toRelative(),
  header: 'In Queue',
  id: 'techWriterQueue',
};

export const TechWriterQueueSort = {
  comparator: dateComparator((i) => i.publicationDate),
  id: 'techWriterQueue',
};

export const TechWriterQueueSelOpt = {
  editable: true,
  id: 'techWriterQueue',
  label: 'In Queue',
  visible: true,
};

export const PublicationsCol = {
  cell: (i: ContentWorkflow) =>
    i.publicationWorkflows.map((wf) => {
      return (
        <div key={wf.state} className="publication-info">
          <Box margin={{ bottom: 'xs' }}>
            <Box display="inline" margin={{ right: 'xs' }}>
              {renderWorkflowInfoLink(wf)}
            </Box>
            <Popover
              size="small"
              position="top"
              triggerType="custom"
              dismissButton={false}
              content={
                <>
                  <div>
                    {wf.approvals} {constants.APPROVALS}
                  </div>
                  <div>
                    {wf.disapprovals} {constants.DISAPPROVALS}
                  </div>
                </>
              }
            >
              <WorkflowStateBadge state={wf.state} />
            </Popover>
          </Box>
        </div>
      );
    }),
  header: 'Current publications',
  id: 'publications',
  minWidth: 135,
  width: 135,
};

export const PublicationsSelOpt = {
  editable: true,
  id: 'publications',
  label: 'Publications',
  visible: true,
};

export const PublicationStateCol = {
  // @ts-expect-error TS2769 - strict migration
  cell: (i: ContentWorkflow) => <WorkflowStateBadge state={i.publicationState} />,
  header: 'Most recent publication state',
  id: 'publicationstate',
  minWidth: 135,
  width: 135,
};

export const PublicationStateSort = {
  comparator: stringComparator((i) => i.publicationState || ''),
  id: 'publicationstate',
};

export const PublicationStateSelOpt = {
  editable: true,
  id: 'publicationstate',
  label: 'Most recent publication state',
  visible: true,
};

export const PublicationVersionCol = {
  cell: (i: ContentWorkflow) => i.publicationWorkflowId,
  header: 'Most recent publication version',
  id: 'publicationVersion',
};

export const PublicationVersionSort = {
  comparator: numberComparator((i) => i.publicationWorkflowId),
  id: 'publicationVersion',
};

export const PublicationVersionSelOpt = {
  editable: true,
  id: 'publicationVersion',
  label: 'Most recent publication version',
  visible: false,
};

export const PublicationWorkflowApprovalsCol = {
  cell: (i: ContentWorkflow) => {
    const pWf = findPublicationWorkflow(i);
    return pWf ? pWf.approvals : i.approvals;
  },
  header: 'Approvals',
  id: 'publicationWorkflowApprovals',
};

export const PublicationWorlflowApprovalsSort = {
  comparator: numberComparator((i) => {
    const pWf = findPublicationWorkflow(i);
    return pWf ? pWf.approvals : i.approvals;
  }),
  id: 'publicationWorkflowApprovals',
};

export const PublicationApprovalsSelOpt = {
  editable: true,
  id: 'publicationWorkflowApprovals',
  label: 'Approvals',
  visible: false,
};

export const UpdatedDateCol = {
  cell: (i: ContentWorkflow) => i.lastDraftUpdate?.toLocaleString(DateTime.DATETIME_FULL),
  header: 'Last draft update',
  id: 'lastdraftupdate',
};

export const UpdateDateSort = {
  comparator: dateComparator((i) => i.lastDraftUpdate),
  id: 'lastdraftupdate',
};

export const UpdateDateSelOpt = {
  editable: true,
  id: 'lastdraftupdate',
  label: 'Last draft updated',
  visible: true,
};

export const ApprovalsCol = {
  cell: (i: ContentWorkflow) => i.approvals,
  header: 'Approvals',
  id: 'approvals',
};

export const ApprovalsSort = {
  comparator: numberComparator((i) => i.approvals),
  id: 'approvals',
};

export const ApprovalsSelOpt = {
  editable: true,
  id: 'approvals',
  label: 'Approvals',
  visible: false,
};

export const RankCol = {
  cell: (i: ContentWorkflow) => i.rank,
  header: 'Rank',
  id: 'rank',
};

export const RankSort = {
  comparator: numberComparator((i) => i.rank),
  id: 'rank',
};

export const RankSelOpt = {
  editable: true,
  id: 'rank',
  label: 'Rank',
  visible: false,
};

export const DisapprovalsCol = {
  cell: (i: ContentWorkflow) => i.disapprovals,
  header: 'Disapprovals',
  id: 'disapprovals',
};

export const DisapprovalsSort = {
  comparator: numberComparator((i) => i.approvals),
  id: 'disapprovals',
};

export const DisapprovalsSelOpt = {
  editable: true,
  id: 'disapprovals',
  label: 'Disapprovals',
  visible: false,
};

export const PublicationWorkflowDisapprovalsCol = {
  cell: (i: ContentWorkflow) => {
    const pWf = findPublicationWorkflow(i);
    return pWf ? pWf.disapprovals : i.disapprovals;
  },
  header: 'Disapprovals',
  id: 'publicationWorkflowDisapprovals',
};

export const PublicationWorkflowDisapprovalsSort = {
  comparator: numberComparator((i) => {
    const pWf = findPublicationWorkflow(i);
    return pWf ? pWf.disapprovals : i.disapprovals;
  }),
  id: 'publicationWorkflowDisapprovals',
};

export const PublicationWorkflowDisapprovalsSelOpt = {
  editable: true,
  id: 'publicationWorkflowDisapprovals',
  label: 'Disapprovals',
  visible: false,
};

export const LastPublicationDateCol = {
  cell: (i: ContentWorkflow) =>
    i.publicationDate ? i.publicationDate.toLocaleString(DateTime.DATETIME_FULL) : '',
  header: 'Last publication',
  id: 'lastpublicationdate',
};

export const LastPublicationDateSort = {
  comparator: dateComparator((i) => i.publicationDate),
  id: 'lastpublicationdate',
};

export const LastPublicationDateSelOpt = {
  editable: true,
  id: 'lastpublicationdate',
  label: 'Last publication',
  visible: false,
};

export const LastAPGPublicationDateCol = {
  cell: (i: ContentWorkflow) =>
    i.apgPublicationDate ? i.apgPublicationDate.toLocaleString(DateTime.DATETIME_FULL) : '',
  header: 'Last APG publication',
  id: 'apgPublicationDate',
};

export const LastAPGPublicationDateSort = {
  comparator: dateComparator((i) => i.apgPublicationDate),
  id: 'apgPublicationDate',
};

export const LastAPGPublicationDateSelOpt = {
  editable: true,
  id: 'apgPublicationDate',
  label: 'Last APG publication',
  visible: false,
};

export const LastAPGPublicationVersionCol = {
  cell: (i: ContentWorkflow) => i.lastApgPublicationId,
  header: 'Last APG publication version',
  id: 'apgpublicationversion',
};

export const LastAPGPublicationVersionSort = {
  comparator: numberComparator((i) => i.lastApgPublicationId),
  id: 'apgpublicationversion',
};

export const LastAPGPublicationVersionSelOpt = {
  editable: true,
  id: 'apgpublicationversion',
  label: 'Last APG publication version',
  visible: false,
};

export const IsTestDataCol = {
  cell: (i: ContentWorkflow) => (i.isTestData ? <Icon name="status-positive" /> : null),
  header: 'Test data',
  id: 'isttestdata',
};

export const IsTestDataSort = {
  comparator: stringComparator((i) => i.isTestData.toString()),
  id: 'isttestdata',
};

export const IsTestDataSelOpt = {
  editable: true,
  id: 'isttestdata',
  label: 'Test data',
  visible: false,
};

export const ReviewRequestActionSelOpt = {
  editable: false,
  id: 'reviewrequestaction',
  label: '',
  visible: true,
};

export const ContainsCodeCol = {
  cell: (i: ContentWorkflow) => {
    const containsCode = !!(i.draftContentTypeMetadata.codeLinks?.length > 0);
    return <div>{containsCode ? <Icon name="status-positive" /> : null}</div>;
  },
  header: 'Contains code',
  id: 'containscode',
};

export const ContainsCodeSort = {
  comparator: booleanComparator((i) => !!(i.draftContentTypeMetadata.codeLinks?.length > 0)),
  id: 'containscode',
};

export const ContainsCodeSelOpt = {
  editable: false,
  id: 'containscode',
  label: 'Contains code',
  visible: true,
};

export const ContentActionCol = {
  cell: (i: ContentWorkflow) => {
    return (
      <div className="awsui-cat-flex awsui-cat-flex-end">
        <EditMetadataButton workflow={i} variant="icon" iconName="settings" />
        <EditingDraftButton workflow={i} />
        <DeleteContentButton
          workflow={i}
          variant="icon"
          iconUrl={'/images/icon-delete.svg'}
          iconAlign="right"
        />
      </div>
    );
  },
  header: '',
  id: 'Action',
};

export const ContentActionColSelOpt = {
  editable: false,
  id: 'Action',
  label: 'Action',
  visible: true,
};

export const CoauthorContentActionCol = {
  cell: (i: ContentWorkflow) => {
    return (
      <div className="awsui-cat-flex awsui-cat-flex-end">
        <EditMetadataButton workflow={i} variant="icon" iconName="settings" />
      </div>
    );
  },
  header: '',
  id: 'Action',
};

export const ReviewerContentActionCol = {
  cell: (i: ContentWorkflow) => {
    return (
      <div className="awsui-cat-flex awsui-cat-flex-end">
        <EditMetadataButton workflow={i} variant="icon" iconName="settings" readOnly />
      </div>
    );
  },
  header: '',
  id: 'Action',
};

export const ProgressCol = {
  cell: (i: ContentWorkflow) => <ProgressDiagram workflow={i} />,
  header: 'Progress',
  id: 'progress',
  minWidth: 200,
  width: 200,
};

export const ProgressColSelOpt = {
  editable: false,
  id: 'progress',
  label: 'Progress',
  visible: true,
};

export const IsLockedCol = {
  cell: (i: ContentWorkflow) =>
    i.lock ? (
      <Box>
        <Icon name="status-positive" /> (by {extractUserAlias(i.lock.acquiredBy)})
      </Box>
    ) : null,
  header: 'Locked',
  id: 'isLocked',
};

export const IsLockedSort = {
  comparator: stringComparator((i) => i.lock?.acquiredBy),
  id: 'isLocked',
};

export const IsLockedSelOpt = {
  editable: true,
  id: 'isLocked',
  label: 'Locked',
  visible: false,
};

export const EngagementsCol = {
  cell: (i: ContentWorkflow) => <ContentEngagementAdminLink workflow={i} />,
  header: 'Engagements',
  id: 'engagements',
};

export const EngagementsSort = {
  comparator: numberComparator((i) => i.engagementCount),
  id: 'engagements',
};

export const EngagementsSelOpt = {
  editable: true,
  id: 'engagements',
  label: 'Engagements',
  visible: false,
};

export const WorkflowTagsCol = {
  cell: (i: ContentWorkflow) => <WorkflowTagBadges workflow={i} />,
  header: 'Tags',
  id: 'tags',
};

export const WorkflowTagsSort = {
  comparator: setComparator((i) => getContentWorkflowTags(i)),
  id: 'tags',
};

export const WorfklowTagsSelOpt = {
  editable: true,
  id: 'tags',
  label: 'Tags',
  visible: false,
};

export const ContentPriorityCol = {
  cell: (i: ContentWorkflow) => contentPriorityDisplayNames.get(i.contentPriority),
  header: (
    <p className="table-column-header" title={constants.PUBLIC_RELEASE_PRIORITY_TOOLTIP}>
      Public release priority
    </p>
  ),
  id: 'contentPriority',
};

export const ContentPrioritySort = {
  comparator: numberComparator((i) => i.contentPriority),
  id: 'contentPriority',
};

export const ContentPrioritySelOpt = {
  editable: true,
  id: 'contentPriority',
  label: 'Public release priority',
  visible: false,
};

const qualityCheckBadgeConfig = new Map<
  QualityCheckStatus,
  { color: 'green' | 'red' | 'grey' | 'blue'; label: string }
>([
  [QualityCheckStatus.COMPLETE, { color: 'green', label: 'QC complete' }],
  [QualityCheckStatus.FAILED, { color: 'red', label: 'QC failed' }],
  [QualityCheckStatus.CANCELLED, { color: 'grey', label: 'QC cancelled' }],
  [QualityCheckStatus.IN_PROGRESS, { color: 'blue', label: 'QC in progress' }],
]);

export const ContentWorkflowCsvFormat: Array<{
  header: string;
  value: (i: ContentWorkflow) => any;
}> = [
  {
    header: 'Content id',
    value: (i) => i.contentId,
  },
  {
    header: 'Content title',
    value: (i) => i.draftContentMetadata.contentName,
  },
  {
    header: 'Content type',
    value: (i) => i.draftContentMetadata.contentType,
  },
  {
    header: 'Intended audience',
    value: (i) => i.contentAudience,
  },
  {
    header: 'Owner',
    value: (i) => i.ownerId,
  },
  {
    header: 'Draft state',
    value: (i) => i.draftState,
  },
  {
    header: 'Publication state',
    value: (i) => i.publicationState || '',
  },
  {
    header: 'Approvals',
    value: (i) => i.approvals,
  },
  {
    header: 'Disapprovals',
    value: (i) => i.disapprovals,
  },
  {
    header: 'Last updated',
    value: (i) => i.lastDraftUpdate?.toISO(),
  },
  {
    header: 'Last publication',
    value: (i) => (i.publicationDate ? i.publicationDate.toISO() : ''),
  },
  {
    header: 'Last APG publication',
    value: (i) => (i.apgPublicationDate ? i.apgPublicationDate.toISO() : ''),
  },
  {
    header: 'Last APG publication version',
    value: (i) => i.lastApgPublicationId,
  },
  {
    header: 'Test data',
    value: (i) => (i.isTestData ? 'yes' : ''),
  },
  {
    header: 'Rank',
    value: (i) => i.rank,
  },
  {
    header: 'Locked',
    value: (i) => (i.lock ? extractUserAlias(i.lock.acquiredBy) : ''),
  },
  {
    header: 'Engagements',
    value: (i) => ((i.engagementCount ?? 0) > 0 ? i.engagementCount : ''),
  },
  {
    header: 'Tags',
    value: (i) => getContentWorkflowTagNames(i),
  },
  {
    header: 'Public release priority',
    value: (i) => i.contentPriority,
  },
];

export const DefaultContentSelectorOptions: Array<
  CollectionPreferencesProps.VisibleContentOption & { visible: boolean }
> = [
  ContentIdSelOpt,
  ContentNameSelOpt,
  ContentTypeSelOpt,
  ContentSubTypeSelOpt,
  ContentAudienceSelOpt,
  ContainsCodeSelOpt,
  ProgressColSelOpt,
  OwnerSelOpt,
  TechWriterSelOpt,
  TechWriterQueueSelOpt,
  DraftStateSelOpt,
  DraftVersionSelOpt,
  PublicationStateSelOpt,
  PublicationVersionSelOpt,
  PublicationsSelOpt,
  UpdateDateSelOpt,
  ApprovalsSelOpt,
  DisapprovalsSelOpt,
  PublicationApprovalsSelOpt,
  PublicationWorkflowDisapprovalsSelOpt,
  LastPublicationDateSelOpt,
  LastAPGPublicationDateSelOpt,
  LastAPGPublicationVersionSelOpt,
  IsTestDataSelOpt,
  RankSelOpt,
  ContentActionColSelOpt,
  IsLockedSelOpt,
  ReviewRequestActionSelOpt,
  EngagementsSelOpt,
  WorfklowTagsSelOpt,
  ContentPrioritySelOpt,
  IsBarRaiserDomainSelOpt,
];

export const DefaultTablePageSizeOptions = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '25',
    value: 25,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
  {
    label: '200',
    value: 200,
  },
  {
    label: '500',
    value: 500,
  },
];

export const DefaultSortableColumns: Array<{
  id: string;
  comparator: (i1: ContentWorkflow, i2: ContentWorkflow) => number;
}> = [
  OwnerSort,
  ContentNameSort,
  ContentIdSort,
  ContentTypeSort,
  ContentSubTypeSort,
  ApprovalsSort,
  DisapprovalsSort,
  PublicationWorlflowApprovalsSort,
  PublicationWorkflowDisapprovalsSort,
  DraftStateSort,
  PublicationStateSort,
  ContentAudienceSort,
  DraftVersionSort,
  PublicationVersionSort,
  UpdateDateSort,
  LastPublicationDateSort,
  LastAPGPublicationDateSort,
  LastAPGPublicationVersionSort,
  IsTestDataSort,
  ContainsCodeSort,
  RankSort,
  TechWriterQueueSort,
  IsLockedSort,
  EngagementsSort,
  TechWriterSort,
  WorkflowTagsSort,
  ContentPrioritySort,
  IsBarRaiserDomainSort,
];
